import { useState, useEffect, useRef } from "react";
import { getSession } from "../service/auth";
import {
  getEmployeeByUserId,
  getAttendanceRecords,
  getOpenAttendance,
  checkIn,
  checkOut,
} from "../service/rpc";
import "../styles/Attendance.css";

/* ── helpers ── */
const pad = (n) => String(n).padStart(2, "0");

const formatDatetime = (dt) => {
  if (!dt) return "—";
  try {
    const d = new Date(dt.replace(" ", "T") + "Z");
    return d.toLocaleString([], {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return dt; }
};

const PAGE_SIZE = 20;

/* ═══════════════════════════════════════
   ATTENDANCE PAGE
═══════════════════════════════════════ */
export default function AttendancePage() {
  const session = getSession();
  const employeeRef = useRef(null);

  const [employee, setEmployee] = useState(null);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [openRecord, setOpenRecord] = useState(null);
  const [liveSecs, setLiveSecs] = useState(0);
  const [viewType, setViewType] = useState("list");

  /* ── Responsive Logic ── */
  useEffect(() => {
    if (window.innerWidth <= 992) {
      setViewType("grid");
    }
  }, []);

  /* ── Core data loader ── */
  const loadData = async (pg = 0) => {
    if (!session?.uid) return;
    setLoading(true);
    try {
      let emp = employeeRef.current;
      if (!emp) {
        emp = await getEmployeeByUserId(session.uid);
        employeeRef.current = emp || null;
        setEmployee(emp || null);
      }

      const domain = emp ? [["employee_id", "=", emp.id]] : [];

      const [{ records: recs, count }, open] = await Promise.all([
        getAttendanceRecords(domain, PAGE_SIZE, pg * PAGE_SIZE),
        emp ? getOpenAttendance(emp.id) : Promise.resolve(null),
      ]);

      setRecords(recs);
      setTotal(count);
      setOpenRecord(open || null);

      if (open) {
        const elapsed = Math.floor(
          (Date.now() - new Date(open.check_in.replace(" ", "T") + "Z").getTime()) / 1000
        );
        setLiveSecs(Math.max(0, elapsed));
      } else {
        setLiveSecs(0);
      }
    } catch (e) {
      console.error("AttendancePage loadData:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(0); }, []);

  const prevPage = useRef(null);
  useEffect(() => {
    if (prevPage.current !== null && prevPage.current !== page) loadData(page);
    prevPage.current = page;
  }, [page]);

  useEffect(() => {
    let iv;
    if (openRecord) iv = setInterval(() => setLiveSecs((s) => s + 1), 1000);
    return () => clearInterval(iv);
  }, [openRecord]);

  const handleCheckIn = async () => {
    const emp = employeeRef.current;
    if (!emp || actionLoading) return;
    setActionLoading(true);
    try { await checkIn(emp.id); setPage(0); await loadData(0); }
    catch (e) { alert("Check-in failed: " + e.message); }
    finally { setActionLoading(false); }
  };

  const handleCheckOut = async () => {
    if (!openRecord || actionLoading) return;
    setActionLoading(true);
    try { await checkOut(openRecord.id); setPage(0); await loadData(0); }
    catch (e) { alert("Check-out failed: " + e.message); }
    finally { setActionLoading(false); }
  };

  const isCheckedIn = !!openRecord;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const hasEmployee = !!employeeRef.current;

  const checkInTime = openRecord
    ? new Date(openRecord.check_in.replace(" ", "T") + "Z")
      .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : null;

  const liveFormatted = `${pad(Math.floor(liveSecs / 3600))}:${pad(Math.floor((liveSecs % 3600) / 60))}:${pad(liveSecs % 60)}`;

  return (
    <div className="att-wrap">

      {/* ── Page Header ── */}
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <h1 className="page-header__title">Attendance</h1>
          <div className="view-toggle">
            <button className={`view-toggle__btn ${viewType === 'list' ? 'view-toggle__btn--active' : ''}`} onClick={() => setViewType('list')}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
              List
            </button>
            <button className={`view-toggle__btn ${viewType === 'grid' ? 'view-toggle__btn--active' : ''}`} onClick={() => setViewType('grid')}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
              </svg>
              Kanban
            </button>
          </div>
        </div>

        {hasEmployee && (
          <div className={`att-status ${isCheckedIn ? "att-status--in" : "att-status--out"}`} style={{ margin: 0 }}>
            {isCheckedIn ? (
              <>
                <div className="att-status__dot att-status__dot--in" />
                <div className="att-status__info">
                  <span className="att-status__label">In {checkInTime}</span>
                  <span className="att-status__timer">{liveFormatted}</span>
                </div>
                <button className="att-action-btn att-action-btn--out" onClick={handleCheckOut} disabled={actionLoading}>
                  {actionLoading ? <span className="att-spinner" /> : (
                    <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>Check out</>
                  )}
                </button>
              </>
            ) : (
              <>
                <div className="att-status__dot att-status__dot--out" />
                <div className="att-status__info">
                  <span className="att-status__label">Out</span>
                  <span className="att-status__timer">00:00:00</span>
                </div>
                <button className="att-action-btn att-action-btn--in" onClick={handleCheckIn} disabled={actionLoading || loading}>
                  {actionLoading ? <span className="att-spinner" /> : (
                    <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" /></svg>Check in</>
                  )}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Records Container ── */}
      <div className={`att-table-card ${loading ? "table-card--loading" : ""}`}>
        {loading && (
          <div className="list-loading-overlay">
            <div className="loader"></div>
            <p style={{ color: "#64748b", fontWeight: "500", fontFamily: "Sora" }}>Loading...</p>
          </div>
        )}

        {viewType === "list" ? (
          <div className="att-table-scroll">
            <table className="att-table">
              <thead>
                <tr>
                  <th className="att-th">Check In</th>
                  <th className="att-th">Check Out</th>
                </tr>
              </thead>
              <tbody>
                {!loading && records.length === 0 ? (
                  <tr><td colSpan={2} className="att-empty">No attendance records found.</td></tr>
                ) : records.map((rec) => {
                  const isOpen = !rec.check_out;
                  return (
                    <tr
                      key={rec.id}
                      className={`att-row ${isOpen ? "att-row--open" : ""}`}
                    >
                      <td className={`att-td ${isOpen ? "att-td--highlight" : ""}`}>{formatDatetime(rec.check_in)}</td>
                      <td className={`att-td ${isOpen ? "att-td--highlight" : ""}`}>
                        {isOpen
                          ? <span className="att-live-badge"><span className="att-live-dot" />Live — {liveFormatted}</span>
                          : formatDatetime(rec.check_out)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="task-grid">
            {!loading && records.length === 0 ? (
              <div className="att-empty" style={{ padding: '40px' }}>No attendance records found.</div>
            ) : records.map((rec) => {
              const isOpen = !rec.check_out;
              const checkInDate = rec.check_in ? rec.check_in.split(' ')[0] : '—';
              const checkInTime = rec.check_in ? rec.check_in.split(' ')[1].slice(0, 5) : '—';
              const checkOutTime = rec.check_out ? rec.check_out.split(' ')[1].slice(0, 5) : '—';

              return (
                <div key={rec.id} className={`kanban-card ${isOpen ? "kanban-card--active" : ""}`}>
                  <div className="att-card__date-label">{checkInDate}</div>
                  
                  <div className="att-card__body">
                    <div className="att-card__field">
                      <label>Check In</label>
                      <span className="att-card__time">{checkInTime}</span>
                    </div>
                    <div className="att-card__field">
                      <label>Check Out</label>
                      <span className="att-card__time">
                        {isOpen ? (
                          <span className="att-live-badge" style={{ fontSize: '15px' }}>{liveFormatted}</span>
                        ) : checkOutTime}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {totalPages > 1 && !loading && (
        <div className="att-pagination">
          <button className="att-page-btn" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>← Prev</button>
          <span className="att-page-info">Page {page + 1} of {totalPages} &nbsp;·&nbsp; {total} records</span>
          <button className="att-page-btn" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>Next →</button>
        </div>
      )}
    </div>
  );
}
